import { useSearchParams } from "react-router-dom";
import { EmptyState, ProductCard, Skeleton } from "@aurelia/ui";
import { useProducts } from "../../features/catalog/queries";
import { useWishlist, useToggleWishlist } from "../../features/wishlist/queries";
import { useDocumentMeta } from "../../lib/useDocumentMeta";

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  useDocumentMeta(
    query ? `Search results for "${query}"` : "Search",
    "Search our full catalog of toys and hair accessories.",
    undefined,
    undefined,
    { noindex: true }
  );
  const { data, isLoading, isError } = useProducts({ search: query }, { enabled: !!query });
  const { data: wishlist } = useWishlist();
  const toggleWishlist = useToggleWishlist();
  const wishlistedIds = new Set(wishlist?.map((p) => p.id));

  return (
    <main className="max-w-7xl mx-auto px-6 py-16">
      <h1 className="text-display-md mb-10">
        {query ? (
          <>
            Search results for <span className="text-accent">&ldquo;{query}&rdquo;</span>
          </>
        ) : (
          "Search"
        )}
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {!query && (
          <EmptyState
            title="Start typing to search"
            description="Use the search bar above to find products by name."
          />
        )}
        {isLoading && Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-square" />)}
        {data?.items.map((p) => (
          <ProductCard
            key={p.id}
            slug={p.slug}
            name={p.name}
            shortDescription={p.shortDescription}
            imageUrl={p.images[0] ?? null}
            basePrice={p.basePrice}
            compareAtPrice={p.compareAtPrice}
            soldCount={p.soldCount}
            stock={p.variants.reduce((sum, v) => sum + v.stock, 0)}
            wishlisted={wishlistedIds.has(p.id)}
            onToggleWishlist={() => toggleWishlist.mutate(p.id)}
          />
        ))}
        {isError && (
          <EmptyState tone="error" title="Couldn't load results" description="Please refresh and try again." />
        )}
        {!isLoading && !isError && query && data?.items.length === 0 && (
          <EmptyState title="No products match your search" description="Try a different name or keyword." />
        )}
      </div>
    </main>
  );
}
